// API client — all requests go to /api/*
// In dev, Vite proxies /api → wrangler dev on :8787
// In production, deployed as Cloudflare Pages Function

const BASE = "/api";

import { getAdminSecret } from "./auth.ts";

// Local dev: wire admin auth automatically.
// In production / real use, the secret is obtained via the login flow.
const ADMIN_SECRET = import.meta.env.VITE_ADMIN_SECRET ?? "local-dev-secret";

async function request<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  // Use secret from auth module (set at login), fall back to env var for local dev
  const secret = getAdminSecret() ?? ADMIN_SECRET;

  const response = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Secret": secret,
      ...options?.headers,
    },
  });

  const data = await response.json() as { success: boolean; data?: T; error?: string };
  if (!data.success) throw new Error(data.error ?? "API error");
  return data.data as T;
}

export const api = {
  // Health / Diagnostics
  health: {
    providers: () =>
      request<Record<string, any>>("/health/providers"),
  },

  // Feeds
  feeds: {
    list: ()           => request<Feed[]>("/feeds"),
    get:  (id: number) => request<Feed>(`/feeds/${id}`),
    create: (body: Partial<Feed>) =>
      request<Feed>("/feeds", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, body: Partial<Feed>) =>
      request<Feed>(`/feeds/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    delete: (id: number) =>
      request<{ deleted: boolean }>(`/feeds/${id}`, { method: "DELETE" }),
  },

  // Articles
  articles: {
    list: (params?: Record<string, string | number>) => {
      const qs = params ? "?" + new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
      ).toString() : "";
      return request<{ articles: Article[]; total: number }>(`/articles${qs}`);
    },
    get: (id: number) =>
      request<{ article: Article; citations: Citation[]; edits: ArticleEdit[] }>(`/articles/${id}`),
    approve:  (id: number, opts?: { draft?: boolean }) =>
      request<{ approved: boolean; draft?: boolean }>(`/articles/${id}/approve`, { method: "POST", body: JSON.stringify(opts ?? {}) }),
    reject: (id: number, note?: string) =>
      request<{ rejected: boolean }>(`/articles/${id}/reject`, {
        method: "POST", body: JSON.stringify({ note }),
      }),
    dismiss: (id: number, note?: string) =>
      request<{ dismissed: boolean }>(`/articles/${id}/dismiss`, {
        method: "POST", body: JSON.stringify({ note }),
      }),
    select: (id: number, tier?: "standard" | "batch") =>
      request<{ queued: boolean }>(`/articles/${id}/select`, {
        method: "POST", body: JSON.stringify({ tier: tier ?? "standard" }),
      }),
    rewrite: (id: number, opts?: { provider?: string; model?: string }) =>
      request<{ queued: boolean }>(`/articles/${id}/rewrite`, {
        method: "POST", body: JSON.stringify(opts ?? {}),
      }),
    rewriteInline: (id: number, opts?: { model?: string }) =>
      request<{ id: number; rewritten_title: string; rewritten_body: string; bluf_subhead: string | null; status: string }>(`/articles/${id}/rewrite-inline`, {
        method: "POST", body: JSON.stringify(opts ?? {}),
      }),
    edit: (id: number, body: Partial<Article>) =>
      request<Article>(`/articles/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    verify_claim: (id: number, claim_text: string, context?: string) =>
      request<VerifyClaimResult>(`/articles/${id}/verify-claim`, {
        method: "POST", body: JSON.stringify({ claim_text, context }),
      }),
    stats: () => request<Record<string, number>>("/articles/stats"),
    browse: (params?: Record<string, string | number>) => {
      const qs = params ? "?" + new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
      ).toString() : "";
      return request<{ articles: BrowseArticle[] }>(`/articles/browse${qs}`);
    },
    curate: (params?: Record<string, string | number>) => {
      const qs = params ? "?" + new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
      ).toString() : "";
      return request<{ articles: BrowseArticle[] }>(`/articles/curate${qs}`);
    },
  },

  // Citations
  citations: {
    list: (articleId: number) => request<Citation[]>(`/articles/${articleId}/citations`),
    update: (id: number, body: Partial<Citation>) =>
      request<Citation>(`/citations/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    add: (articleId: number, body: Partial<Citation>) =>
      request<Citation>(`/articles/${articleId}/citations`, { method: "POST", body: JSON.stringify(body) }),
  },

  // Settings
  settings: {
    get:    ()                        => request<Record<string, string>>("/settings"),
    update: (body: Record<string, unknown>) =>
      request<{ updated: boolean }>("/settings", { method: "PATCH", body: JSON.stringify(body) }),
  },

  // Prompts
  prompts: {
    list: ()              => request<Prompt[]>("/prompts"),
    get:  (name: string)  => request<Prompt | null>(`/prompts/${name}`),
    upsert: (name: string, body: string) =>
      request<Prompt>(`/prompts/${name}`, { method: "PUT", body: JSON.stringify({ body }) }),
  },

  // Triggers
  triggers: {
    purgeContentMissing: () =>
      request<{ triggered: boolean; deleted: number; message: string }>("/trigger/purge-content-missing", { method: "POST" }),
    purgeByStatus: (status: string) =>
      request<{ triggered: boolean; deleted: number; message: string }>(`/trigger/purge-status/${status}`, { method: "POST" }),
    poll: () =>
      request<{ triggered: boolean; message: string }>("/trigger/poll", { method: "POST" }),
  },

  // Audit log
  audit: {
    list: (params?: { limit?: number; article_id?: number }) => {
      const qs = params ? "?" + new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
      ).toString() : "";
      return request<AuditEntry[]>(`/audit${qs}`);
    },
  },
};

// ── Type definitions ──────────────────────────────────────────────────────────
export interface Feed {
  id: number;
  name: string;
  url: string;
  type: "rss" | "webhook" | "mediastack";
  polling_interval_minutes: number;
  active: number;
  import_mode: "auto" | "manual";
  curation_enabled: number;
  target_ghost_instance: "news" | "evergreen";
  default_ghost_routing_tags: string;
  default_ghost_category_tags: string;
  content_fetch_strategy: string;
  rewrite_provider: string | null;
  source_type: string;
  processing_tier: "standard" | "batch";
  default_style_mode: "a" | "b";
  companion_enabled: number;
  created_at: string;
  updated_at: string;
}

export interface Article {
  id: number;
  feed_id: number;
  guid: string;
  original_url: string;
  original_title: string;
  original_body: string | null;
  original_author: string | null;
  original_published_at: string | null;
  rewritten_title: string | null;
  rewritten_body: string | null;
  rewrite_model: string | null;
  rewrite_tokens: number | null;
  rewrite_cost_usd: number | null;
  content_fetch_source: string | null;
  content_fetch_attempted: number;
  content_fetch_error: string | null;
  rss_excerpt_only: number;
  status: string;
  ghost_post_id: string | null;
  ghost_instance: string | null;
  ghost_publish_url: string | null;
  approved_by: string | null;
  approved_at: string | null;
  published_at: string | null;
  rejection_note: string | null;
  style_mode: "a" | "b";
  bluf_subhead: string | null;
  deck: string | null;
  pull_quote: string | null;
  pull_quote_accepted: number;
  companion_article_id: number | null;
  ghost_routing_tags: string;
  ghost_category_tags: string;
  processing_tier: "standard" | "batch";
  show_original_source: number;
  created_at: string;
  updated_at: string;
  feed_name?: string;
  feed_source_type?: string;
}

export interface BrowseArticle {
  id: number;
  feed_id: number;
  original_title: string;
  original_url: string;
  original_author: string | null;
  original_published_at: string | null;
  original_body: string | null;
  status: string;
  created_at: string;
  feed_name: string;
  source_type: string;
}

export interface Citation {
  id: number;
  article_id: number;
  source_text: string;
  source_url: string | null;
  source_type: string;
  publish_default: number;
  publish_include: number;
  added_manually: number;
  created_at: string;
}

export interface ArticleEdit {
  id: number;
  article_id: number;
  edit_type: "manual" | "ai_on_demand";
  editor: string;
  instruction: string | null;
  replacement_text: string | null;
  accepted: number;
  created_at: string;
}

export interface Prompt {
  id: number;
  name: string;
  body: string;
  active: number;
  created_at: string;
  updated_at: string;
}

export interface AuditEntry {
  id: number;
  article_id: number | null;
  event_type: string;
  actor: string;
  summary: string;
  created_at: string;
}

export interface VerifyClaimResult {
  verifiability: "verifiable" | "contested" | "unverifiable";
  notes: string;
  suggested_sources: string[];
}
